import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Maximize, Minimize, ZoomOut } from "lucide-react";
import { Onboard3D } from "@/components/telemetry/onboard/Onboard3D";
import { ChannelChart, DeltaChart } from "@/components/telemetry/TelemetryCharts";
import { MapPanel } from "@/components/telemetry/MapPanel";
import { LapStats } from "@/components/telemetry/LapStats";
import { isGpsChannel } from "@/lib/geo";
import { useT } from "@/lib/i18n";
import { useSettings } from "@/context/SettingsContext";
import { axisGrid, sectorMarks } from "@/lib/telemetry";

export const chip = (active) => `px-2.5 py-1 font-mono text-[11px] uppercase border rounded-sm transition-colors duration-200 ${active ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10" : "border-[#282D3D] text-slate-400 hover:text-white"}`;

export function useFullscreen() {
  const [fs, setFs] = useState(false);
  const [vh, setVh] = useState(window.innerHeight);
  useEffect(() => {
    if (!fs) {
      return undefined;
    }
    document.documentElement.requestFullscreen?.().catch(() => {});
    const onResize = () => setVh(window.innerHeight);
    const onKey = (e) => { if (e.key === "Escape") setFs(false); };
    const onChange = () => { if (!document.fullscreenElement) setFs(false); };
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onChange);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("fullscreenchange", onChange);
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
  }, [fs]);
  return [fs, setFs, vh];
}

function useFitMap(fs, vh) {
  const slot = useRef(null);
  const tries = useRef(0);
  const [mapH, setMapH] = useState(Math.round(vh * 0.4));
  useLayoutEffect(() => {
    if (!fs || !slot.current) {
      return undefined;
    }
    const fit = () => {
      const chrome = slot.current.firstElementChild.offsetHeight - mapH;
      const h = Math.max(160, Math.floor(slot.current.clientHeight - chrome));
      if (Math.abs(h - mapH) > 2 && tries.current < 6) {
        tries.current += 1;
        setMapH(h);
      }
    };
    fit();
    const ro = new ResizeObserver((entries) => { if (entries.some((e) => e.target === slot.current)) tries.current = 0; fit(); });
    ro.observe(slot.current);
    ro.observe(slot.current.firstElementChild);
    return () => ro.disconnect();
  }, [fs, mapH]);
  return { slot, mapH };
}

export const TelemetryAnalysis = ({ lapA, lapB, lapC = null, channels, trackId, mapKey, sessionId, setForm, csvTrack }) => {
  const { t } = useT();
  const chartChannels = channels.filter((c) => !isGpsChannel(c));
  const prefs = (useSettings().settings.default_channels || []).map((p) => chartChannels.find((c) => c.toLowerCase() === p.toLowerCase())).filter(Boolean);
  const defaults = prefs.length ? prefs : chartChannels.filter((c) => /speed|rpm/i.test(c));
  const [shown, setShown] = useState(defaults.length ? defaults : chartChannels.slice(0, 2));
  const [axis, setAxis] = useState("t");
  const [fs, setFs, vh] = useFullscreen();
  const [show3d, setShow3d] = useState(false);
  const fit = useFitMap(fs, vh);
  const [zoom, setZoom] = useState(null);
  useEffect(() => { setZoom(null); }, [axis, lapA]);
  const grid = useMemo(() => axisGrid([lapA, lapB, lapC], axis, zoom), [lapA, lapB, lapC, axis, zoom]);
  const marks = useMemo(() => sectorMarks(lapA), [lapA]);
  const toggle = (c) => setShown(shown.includes(c) ? shown.filter((x) => x !== c) : [...shown, c]);
  const visible = shown.filter((c) => chartChannels.includes(c));
  const deltaH = fs ? Math.max(90, vh * 0.14) : 170;
  const anyCmp = lapB || lapC;
  const chartH = fs ? Math.max(90, (vh - 160 - (anyCmp ? deltaH + 52 : 0)) / Math.max(1, visible.length) - 52) : 200;

  const header = (
    <div data-testid="telemetry-analysis-header" className="flex flex-wrap items-center gap-3 border-b border-[#282D3D] pb-3">
      <span className="font-heading uppercase font-bold text-xl tracking-wide">{t("analysis")}</span>
      <span className="hidden md:flex items-center gap-3 min-w-0 font-mono text-[11px]">
        <span className="flex items-center gap-1.5 min-w-0 text-slate-300"><span className="w-2.5 h-2.5 rounded-full bg-[#007AFF] shrink-0" /><span className="truncate">{lapA.label || `${t("lap_col")} ${lapA.lap}`}</span></span>
        {lapB && <span className="flex items-center gap-1.5 min-w-0 text-slate-300"><span className="w-2.5 h-2.5 rounded-full bg-[#FF3B30] shrink-0" /><span className="truncate">{lapB.label || `${t("lap_col")} ${lapB.lap}`}</span></span>}
        {lapC && <span data-testid="analysis-legend-c" className="flex items-center gap-1.5 min-w-0 text-slate-300"><span className="w-2.5 h-2.5 rounded-full bg-[#22D3EE] shrink-0" /><span className="truncate">{lapC.label || `${t("lap_col")} ${lapC.lap}`}</span></span>}
      </span>
      <button type="button" data-testid="onboard-3d-btn" onClick={() => setShow3d(true)} disabled={!lapA.x?.length && !lapA.lat?.length}
        className="ml-auto shrink-0 flex items-center gap-2 h-9 px-4 rounded-sm border border-[#3B82F6]/60 text-[#93C5FD] font-heading uppercase font-bold tracking-wide hover:bg-[#3B82F6]/15 disabled:opacity-40 transition-colors duration-200">
        <Box className="w-4 h-4" /> {t("onboard_3d")}
      </button>
      <button type="button" data-testid="telemetry-fullscreen-btn" onClick={() => setFs(!fs)} title={fs ? "Esc" : ""}
        className="shrink-0 flex items-center gap-2 h-9 px-4 rounded-sm bg-[#FFB800] text-[#090A0F] font-heading uppercase font-bold tracking-wide hover:bg-[#ffc933] active:scale-[0.97] transition-[background-color,transform] duration-200">
        {fs ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />} {fs ? t("exit_fullscreen_esc") : t("telemetry_fullscreen")}
      </button>
      {show3d && <Onboard3D lapA={lapA} lapB={lapB} axis={axis} trackId={trackId} onClose={() => setShow3d(false)} />}
    </div>
  );
  const controls = (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-[10px] uppercase text-slate-500 mr-1">{t("channels")}</span>
      {chartChannels.map((c) => <button type="button" key={c} data-testid={`telemetry-channel-${c}`} onClick={() => toggle(c)} className={chip(shown.includes(c))}>{c}</button>)}
      <span className="ml-auto flex items-center gap-1.5">
        {zoom ? (
          <button type="button" data-testid="telemetry-zoom-reset-btn" onClick={() => setZoom(null)} className={`${chip(true)} flex items-center gap-1.5 mr-2`}>
            <ZoomOut className="w-3.5 h-3.5" /> {t("zoom_reset")} · {zoom[0].toFixed(axis === "t" ? 2 : 0)}–{zoom[1].toFixed(axis === "t" ? 2 : 0)} {axis === "t" ? "s" : "m"}
          </button>
        ) : <span data-testid="telemetry-zoom-hint" className="hidden lg:inline font-mono text-[10px] text-slate-500 mr-2">{t("zoom_hint")}</span>}
        <span className="font-mono text-[10px] uppercase text-slate-500 mr-1">{t("x_axis")}</span>
        <button type="button" data-testid="axis-time-btn" onClick={() => setAxis("t")} className={chip(axis === "t")}>{t("axis_time")}</button>
        <button type="button" data-testid="axis-distance-btn" onClick={() => setAxis("d")} className={chip(axis === "d")}>{t("axis_distance")}</button>
      </span>
    </div>
  );
  const charts = (
    <>
      {anyCmp && <DeltaChart lapA={lapA} lapB={lapB} lapC={lapC} axis={axis} marks={marks} h={deltaH} zoom={zoom} onZoom={setZoom} />}
      {visible.map((c) => <ChannelChart key={c} channel={c} lapA={lapA} lapB={lapB} lapC={lapC} grid={grid} axis={axis} marks={marks} h={chartH} zoom={zoom} onZoom={setZoom} />)}
    </>
  );
  const map = (h) => (
    <MapPanel sessionId={sessionId} trackId={trackId} setForm={setForm} lap={lapA} lapB={lapB} lapC={lapC} channels={channels}
      csvTrack={csvTrack} mapKey={mapKey} axis={axis} marks={marks} mapH={h} fill={fs} />
  );

  if (fs) {
    return createPortal(
      <div data-testid="telemetry-fullscreen" className="fixed inset-0 z-[60] bg-[#090A0F] p-3 overflow-hidden flex flex-col gap-3">
        {header}
        <div className="flex-1 min-h-0 grid grid-cols-[minmax(0,1fr)_minmax(0,40%)] gap-3">
          <div className="min-w-0 flex flex-col gap-2 overflow-y-auto pr-1">{controls}{charts}</div>
          <div className="min-w-0 min-h-0 flex flex-col gap-3 overflow-hidden">
            <div ref={fit.slot} className="flex-1 basis-0 min-h-0 overflow-hidden"><div>{map(fit.mapH)}</div></div>
            <div className="shrink-0"><LapStats lapA={lapA} lapB={lapB} lapC={lapC} channels={channels} compact /></div>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return (
    <div className="space-y-4">
      {header}
      {controls}
      <LapStats lapA={lapA} lapB={lapB} lapC={lapC} channels={channels} />
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_520px] gap-4 items-start">
        <div className="space-y-3 min-w-0">{charts}</div>
        <div className="xl:sticky xl:top-20 self-start min-w-0">{map(460)}</div>
      </div>
    </div>
  );
};
